import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Select,
    Space,
    Table,
    Tabs,
    Tag,
    message
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, SettingOutlined } from '@ant-design/icons';
import {
    deleteGlobalRoleSystemAPI,
    getGlobalAccessConfigurationAPI,
    postGlobalModuleAPI,
    postGlobalRoleAPI,
    postGlobalRoleSystemAPI,
    postGlobalSystemAPI,
    putGlobalModuleAPI,
    putGlobalRoleAPI,
    putGlobalRoleSystemModulesAPI,
    putGlobalSystemAPI
} from '../store/user/restCallUser';

type Row = Record<string, any>;
type Entity = 'system' | 'role' | 'module' | 'role-system' | null;

const responseData = (response: any): any => response?.Response?.data ?? response?.Response ?? response?.data ?? response;
const responseError = (response: any): string | null => {
    if (!response) return 'No se obtuvo respuesta del servidor.';
    if (response.Error !== null && response.Error !== undefined) return response.Error?.message || response.Error;
    if (response.StatusCode !== undefined && (response.StatusCode < 200 || response.StatusCode >= 300)) return 'La operación no pudo completarse.';
    return null;
};
const list = (value: any): Row[] => Array.isArray(value) ? value : [];
const idOf = (row: Row) => String(row.id || row.Id || '');

const emptyConfig = { systems: [] as Row[], roles: [] as Row[], modules: [] as Row[], roleSystems: [] as Row[] };

function GlobalAccessConfiguration() {
    const [config, setConfig] = useState(emptyConfig);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [entity, setEntity] = useState<Entity>(null);
    const [editing, setEditing] = useState<Row | null>(null);
    const [roleSystemModules, setRoleSystemModules] = useState<string[]>([]);
    const [form] = Form.useForm();
    const [messageApi, contextHolder] = message.useMessage();

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getGlobalAccessConfigurationAPI();
            const failure = responseError(response);
            if (failure) throw new Error(failure);
            const data = responseData(response) || {};
            setConfig({
                systems: list(data.systems || data.Systems),
                roles: list(data.roles || data.Roles),
                modules: list(data.modules || data.Modules),
                roleSystems: list(data.roleSystems || data.RoleSystems)
            });
        } catch (e: any) {
            setError(e?.message || 'No se pudo cargar la configuración global de acceso.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { void load(); }, [load]);

    const close = () => {
        setEntity(null);
        setEditing(null);
        setRoleSystemModules([]);
        form.resetFields();
    };

    const open = (type: Entity, row?: Row) => {
        setEntity(type);
        setEditing(row || null);
        if (type === 'role-system') {
            setRoleSystemModules(list(row?.modules || row?.Modules).map(idOf));
            return;
        }
        form.setFieldsValue(row ? { ...row, keyModule: row.keyModule || row.KeyModule } : {
            status: 'ACTIVO', visible: true, sequential: 0, portal: false, xTenant: 'TBC'
        });
    };

    const save = async (values: Row) => {
        try {
            let response: any;
            if (entity === 'system') response = editing ? await putGlobalSystemAPI(idOf(editing), values) : await postGlobalSystemAPI(values);
            if (entity === 'role') response = editing ? await putGlobalRoleAPI(idOf(editing), values) : await postGlobalRoleAPI(values);
            if (entity === 'module') response = editing ? await putGlobalModuleAPI(idOf(editing), values) : await postGlobalModuleAPI(values);
            if (entity === 'role-system') response = await postGlobalRoleSystemAPI(values);
            const failure = responseError(response);
            if (failure) throw new Error(failure);
            messageApi.success(editing ? 'Configuración actualizada.' : 'Configuración creada.');
            close();
            await load();
        } catch (e: any) {
            setError(e?.message || 'No se pudo guardar la configuración.');
        }
    };

    const saveRoleSystemModules = async () => {
        if (!editing) return;
        try {
            const response = await putGlobalRoleSystemModulesAPI(idOf(editing), { moduleIds: roleSystemModules });
            const failure = responseError(response);
            if (failure) throw new Error(failure);
            messageApi.success('Módulos del rol configurados.');
            close();
            await load();
        } catch (e: any) {
            setError(e?.message || 'No se pudieron guardar los módulos del rol.');
        }
    };

    const removeRoleSystem = async (row: Row) => {
        try {
            const response = await deleteGlobalRoleSystemAPI(idOf(row));
            const failure = responseError(response);
            if (failure) throw new Error(failure);
            messageApi.success('Asociación eliminada.');
            await load();
        } catch (e: any) {
            setError(e?.message || 'No se pudo eliminar la asociación.');
        }
    };

    const statusTag = (value: string) => <Tag color={String(value).toUpperCase() === 'ACTIVO' ? 'green' : 'default'}>{value}</Tag>;
    const actionColumn = (type: Entity) => ({
        title: 'Acciones',
        key: 'actions',
        render: (_: unknown, row: Row) => <Button size="small" icon={<EditOutlined />} onClick={() => open(type, row)}>Editar</Button>
    });

    const systemsColumns = [
        { title: 'Clave', dataIndex: 'key' },
        { title: 'Nombre', dataIndex: 'label' },
        { title: 'URL', dataIndex: 'url' },
        { title: 'Tenant', dataIndex: 'xTenant' },
        { title: 'Portal', dataIndex: 'portal', render: (value: boolean) => value ? 'Sí' : 'No' },
        actionColumn('system')
    ];
    const rolesColumns = [
        { title: 'Nombre', dataIndex: 'name' },
        { title: 'Descripción', dataIndex: 'description' },
        { title: 'Creado', dataIndex: 'createdAt', render: (value: string) => value ? new Date(value).toLocaleDateString() : '' },
        actionColumn('role')
    ];
    const modulesColumns = [
        { title: 'Clave', dataIndex: 'keyModule' },
        { title: 'Nombre', dataIndex: 'label' },
        { title: 'URL', dataIndex: 'url' },
        { title: 'Orden', dataIndex: 'sequential' },
        { title: 'Estado', dataIndex: 'status', render: statusTag },
        actionColumn('module')
    ];
    const roleSystemsColumns = [
        { title: 'Sistema', dataIndex: 'systemLabel', render: (_: string, row: Row) => `${row.systemLabel} (${row.systemKey})` },
        { title: 'Rol', dataIndex: 'rolName' },
        { title: 'Módulos habilitados', dataIndex: 'modules', render: (value: Row[]) => list(value).length },
        {
            title: 'Acciones', key: 'actions', render: (_: unknown, row: Row) => <Space>
                <Button size="small" icon={<SettingOutlined />} onClick={() => open('role-system', row)}>Módulos</Button>
                <Popconfirm title="¿Eliminar esta asociación?" description="Solo se permite si no tiene usuarios, contratos ni módulos asignados." onConfirm={() => void removeRoleSystem(row)} okText="Eliminar" cancelText="Cancelar">
                    <Button size="small" danger icon={<DeleteOutlined />}>Eliminar</Button>
                </Popconfirm>
            </Space>
        }
    ];

    const table = (data: Row[], columns: any[], type: Entity, label: string) => <>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}><Button type="primary" icon={<PlusOutlined />} onClick={() => open(type)}>{label}</Button></div>
        <Table rowKey={idOf} loading={loading} dataSource={data} columns={columns} pagination={{ pageSize: 8 }} />
    </>;

    const entityFields = () => {
        if (entity === 'system') return <>
            <Form.Item name="key" label="Clave" rules={[{ required: true, message: 'La clave es obligatoria' }]}><Input maxLength={150} placeholder="SALES_DOCUMENT" /></Form.Item>
            <Form.Item name="label" label="Nombre" rules={[{ required: true }]}><Input maxLength={150} /></Form.Item>
            <Form.Item name="url" label="URL del portal" rules={[{ required: true }]}><Input maxLength={255} /></Form.Item>
            <Form.Item name="icon" label="Icono" rules={[{ required: true }]}><Input maxLength={150} /></Form.Item>
            <Form.Item name="xTenant" label="Tenant" rules={[{ required: true }]}><Input maxLength={10} /></Form.Item>
            <Form.Item name="portal" label="Es portal"><Select options={[{ value: true, label: 'Sí' }, { value: false, label: 'No' }]} /></Form.Item>
            <Form.Item name="description" label="Descripción"><Input.TextArea rows={2} maxLength={255} /></Form.Item>
        </>;
        if (entity === 'role') return <>
            <Form.Item name="name" label="Nombre del rol" rules={[{ required: true }]}><Input maxLength={150} /></Form.Item>
            <Form.Item name="description" label="Descripción"><Input.TextArea rows={3} maxLength={255} /></Form.Item>
        </>;
        if (entity === 'module') return <>
            <Form.Item name="keyModule" label="Clave" rules={[{ required: true }]}><Input maxLength={150} /></Form.Item>
            <Form.Item name="label" label="Nombre" rules={[{ required: true }]}><Input maxLength={150} /></Form.Item>
            <Form.Item name="url" label="Ruta/URL" rules={[{ required: true }]}><Input maxLength={150} /></Form.Item>
            <Form.Item name="icon" label="Icono"><Input maxLength={150} /></Form.Item>
            <Form.Item name="sequential" label="Orden" rules={[{ required: true }]}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
            <Form.Item name="status" label="Estado" rules={[{ required: true }]}><Select options={[{ value: 'ACTIVO' }, { value: 'INACTIVO' }]} /></Form.Item>
            <Form.Item name="visible" label="Visible" rules={[{ required: true }]}><Select options={[{ value: true, label: 'Sí' }, { value: false, label: 'No' }]} /></Form.Item>
            <Form.Item name="description" label="Descripción"><Input.TextArea rows={2} maxLength={255} /></Form.Item>
        </>;
        return <>
            <Form.Item name="rolId" label="Rol" rules={[{ required: true, message: 'Seleccione un rol' }]}><Select options={config.roles.map(row => ({ value: idOf(row), label: row.name }))} showSearch optionFilterProp="label" /></Form.Item>
            <Form.Item name="systemId" label="Sistema" rules={[{ required: true, message: 'Seleccione un sistema' }]}><Select options={config.systems.map(row => ({ value: idOf(row), label: `${row.label} (${row.key})` }))} showSearch optionFilterProp="label" /></Form.Item>
        </>;
    };

    const title = entity === 'system' ? 'sistema' : entity === 'role' ? 'rol' : entity === 'module' ? 'módulo' : 'asociación rol-sistema';
    return <Card title="Configuración global de sistemas, módulos y roles" extra={<Button icon={<ReloadOutlined />} onClick={() => void load()}>Actualizar</Button>}>
        {contextHolder}
        <Alert type="info" showIcon message="Administración exclusiva de Blue" description="Aquí se registran los sistemas y sus módulos de configuración, se crean los roles y se habilitan módulos para cada relación rol-sistema. Gestión de contratos utiliza estas relaciones, pero mantiene sus configuraciones comerciales por organización." style={{ marginBottom: 16 }} />
        {error && <Alert type="error" showIcon closable message={error} onClose={() => setError(null)} style={{ marginBottom: 16 }} />}
        <Tabs items={[
            { key: 'systems', label: 'Sistemas', children: table(config.systems, systemsColumns, 'system', 'Nuevo sistema') },
            { key: 'roles', label: 'Roles', children: table(config.roles, rolesColumns, 'role', 'Nuevo rol') },
            { key: 'modules', label: 'Módulos de configuración', children: table(config.modules, modulesColumns, 'module', 'Nuevo módulo') },
            { key: 'role-systems', label: 'Roles por sistema', children: table(config.roleSystems, roleSystemsColumns, 'role-system', 'Asociar rol a sistema') }
        ]} />
        <Modal open={entity !== null && entity !== 'role-system'} title={`${editing ? 'Editar' : 'Nuevo'} ${title}`} onCancel={close} footer={null} destroyOnClose>
            <Form form={form} layout="vertical" onFinish={(values) => void save(values)}>
                {entityFields()}
                <Space><Button onClick={close}>Cancelar</Button><Button type="primary" htmlType="submit">Guardar</Button></Space>
            </Form>
        </Modal>
        <Modal open={entity === 'role-system' && !!editing} title="Módulos habilitados para el rol" onCancel={close} onOk={() => void saveRoleSystemModules()} okText="Guardar" cancelText="Cancelar" destroyOnClose>
            <Select mode="multiple" value={roleSystemModules} onChange={setRoleSystemModules} options={config.modules.map(row => ({ value: idOf(row), label: `${row.label} (${row.keyModule})` }))} style={{ width: '100%' }} placeholder="Seleccione los módulos" optionFilterProp="label" showSearch />
        </Modal>
        <Modal open={entity === 'role-system' && !editing} title="Asociar rol a sistema" onCancel={close} footer={null} destroyOnClose>
            <Form form={form} layout="vertical" onFinish={(values) => void save(values)}>{entityFields()}<Space><Button onClick={close}>Cancelar</Button><Button type="primary" htmlType="submit">Guardar</Button></Space></Form>
        </Modal>
    </Card>;
}

export default GlobalAccessConfiguration;
